import { useState, useEffect } from 'react'
import TableMui from 'src/components/TableMui'
import { Button } from '@mui/material'
import { useDispatch } from 'react-redux'
import { useSelector } from 'src/utils/store'
import { defaultFulfillmentCenterTypeFilters } from 'src/features/fulfillmentCenterFilters'
import { actions } from 'src/features/dock-management/DockManagementState'
import { DocksData, DockStatus } from 'src/features/dock-management/DockManagementTypes'
import { State } from 'src/features/types'
import ModalChangeDockStatus from '../ModalChangeDockStatus/ModalChangeDockStatus'
import { actions as appActions } from '../../../state'
import SwitchMui from 'src/components/SwitchMui/SwitchMui'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'
import * as S from './DocksTable.styles'
import { toast } from 'react-toastify'

interface ModalConfirmationData {
  title: string
  description: string
  onConfirmAction: () => void
}

const DocksTable = () => {
  const dispatch = useDispatch()
  const dockState = useSelector((state: State) => state.dockManagement.docks)

  const [showModalChangeDockStatus, setShowModalChangeDockStatus] = useState<boolean>(false)
  const [idDock, setIdDock] = useState<number>(0)
  const [dockCode, setDockCode] = useState<string>('')
  const [showDockModalConfirmation, setShowDockModalConfirmation] = useState<boolean>(false)
  const [confirmationModalType, setConfirmationModalType] = useState<'type' | 'express'>('type')
  const [isFresh, setIsFresh] = useState<boolean>(false)
  const [isExpress, setIsExpress] = useState<boolean>(false)
  const [preventDoubleRequest, setPreventDoubleRequest] = useState<boolean>(false)

  const openModal = (selectedIdDock: number, selectedDockCode: string) => {
    setShowModalChangeDockStatus(true)
    setIdDock(selectedIdDock)
    setDockCode(selectedDockCode)
  }

  const onConfirm = () => {
    dispatch(actions.patchDock(idDock))
  }

  useEffect(() => {
    dispatch(actions.getDocks({}))
    dispatch(appActions.getAvailableFulfillmentCenters(defaultFulfillmentCenterTypeFilters))
  }, [dispatch])

  const onChangeType = (selectedIdDock: number, selectedDockCode: string, selectedIsFresh: boolean) => {
    if (preventDoubleRequest) {
      return
    }

    if (!showDockModalConfirmation) {
      setIdDock(selectedIdDock)
      setDockCode(selectedDockCode)
      setIsFresh(selectedIsFresh)
      setConfirmationModalType('type')
      setShowDockModalConfirmation(true)
      return
    }

    setPreventDoubleRequest(true)

    const onSuccess = () => {
      setPreventDoubleRequest(false)
      setShowDockModalConfirmation(false)
      toast.success(`Doca ${selectedDockCode} foi definida como ${selectedIsFresh ? 'Fresh' : 'Mercearia'}.`)
    }

    const onError = () => {
      setPreventDoubleRequest(false)
    }

    dispatch(actions.setDockIsFresh({
      id: selectedIdDock,
      isFresh: selectedIsFresh,
      onSuccess,
      onError,
    }))
  }

  const onChangeExpress = (selectedIdDock: number, selectedDockCode: string, selectedIsExpress: boolean) => {
    if (preventDoubleRequest) {
      return
    }

    if (!showDockModalConfirmation) {
      setIdDock(selectedIdDock)
      setDockCode(selectedDockCode)
      setIsExpress(selectedIsExpress)
      setConfirmationModalType('express')
      setShowDockModalConfirmation(true)
      return
    }

    setPreventDoubleRequest(true)

    const onSuccess = () => {
      setPreventDoubleRequest(false)
      setShowDockModalConfirmation(false)
      toast.success(`Doca ${selectedDockCode} foi definida como ${selectedIsExpress ? 'Expressa' : 'Normal'}.`)
    }

    const onError = () => {
      setPreventDoubleRequest(false)
    }

    dispatch(actions.setDockIsExpress({
      id: selectedIdDock,
      isExpress: selectedIsExpress,
      onSuccess,
      onError,
    }))
  }

  const modalConfirmationData: Record<string, ModalConfirmationData> = {
    type: {
      title: 'Confirmação de Fresh/Mercearia',
      description: `Doca ${dockCode} será definida como ${isFresh ? 'Fresh' : 'Mercearia'}.`,
      onConfirmAction: () => onChangeType(idDock, dockCode, isFresh),
    },
    express: {
      title: 'Confirmação de Expressa',
      description: `Doca ${dockCode} será definida como ${isExpress ? 'Expressa' : 'Normal'}.`,
      onConfirmAction: () => onChangeExpress(idDock, dockCode, isExpress),
    },
  }

  const resolveTableRows = () => dockState?.map((dock: DocksData) => {
    const disabled = dock.status === DockStatus.FREE
    const statusCapitalized = dock.status.charAt(0) + dock.status.slice(1).toLocaleLowerCase()

    return {
      dockCode: dock.dockCode,
      status: statusCapitalized,
      groceryLocation: dock.groceryLocation,
      freshLocation: dock.freshLocation,
      devolLocation: dock.devolLocation,
      fulfillmentCenterName: dock.fulfillmentCenterName,
      currentReceivementId: dock.currentReceivementId,
      type: (
        <SwitchMui
          checked={dock.isFresh}
          onClick={() => onChangeType(dock.id, dock.dockCode, !dock.isFresh)}
          offText='Mercearia'
          onText='Fresh'
          fullWidth
          testId='switch-dock-type-test-id'
        />
      ),
      express: (
        <SwitchMui
          checked={dock.isExpress}
          onClick={() => onChangeExpress(dock.id, dock.dockCode, !dock.isExpress)}
          offText='Não'
          onText='Sim'
          fullWidth
          testId='switch-dock-express-test-id'
        />
      ),
      action: <Button data-testid='btn-set-dock-free' variant='outlined' size='small' onClick={() => openModal(dock.id, dock.dockCode)} disabled={disabled}>Liberar</Button>,
    }
  })

  return (
    <>
      <TableMui
        columns={[
          { id: 'dockCode', label: 'Código', testId: 'dock-code-test-id' },
          { id: 'groceryLocation', label: 'Endereço Mercearia', testId: 'dock-grocery-address-test-id' },
          { id: 'freshLocation', label: 'Endereço Fresh', testId: 'dock-fresh-address-test-id' },
          { id: 'devolLocation', label: 'Endereço de Devolução', testId: 'dock-devol-address-test-id' },
          { id: 'fulfillmentCenterName', label: 'Fulfillment Center', testId: 'fulfillment-center-name-test-id' },
          { id: 'currentReceivementId', label: 'Recebimento Atual', testId: 'current-receivement-id-test-id' },
          { id: 'type', label: 'Tipo', testId: 'dock-type-test-id' },
          { id: 'express', label: 'Expressa', testId: 'dock-express-test-id' },
          { id: 'status', label: 'Status', testId: 'dock-status-test-id' },
          { id: 'action', label: 'Ação' },
        ]}
        rows={resolveTableRows()}
        enablePagination
      />
      <ModalChangeDockStatus
        confirmAction={onConfirm}
        setIsOpen={setShowModalChangeDockStatus}
        isOpen={showModalChangeDockStatus}
        dockCode={dockCode}
      />
      <ConfirmOrRejectModal
        show={showDockModalConfirmation}
        title={modalConfirmationData[confirmationModalType].title}
        customizableDescription={(
          <S.ContainerDescription>
            <p>
              {modalConfirmationData[confirmationModalType].description}
            </p>
            <p>
              Deseja prosseguir?
            </p>
          </S.ContainerDescription>
        )}
        onConfirm={modalConfirmationData[confirmationModalType].onConfirmAction}
        onReject={() => setShowDockModalConfirmation(false)}
        onClose={() => setShowDockModalConfirmation(false)}
        dataTestId='dock-modal-confirm-test-id'
      />
    </>
  )
}

export default DocksTable
